"use client"

import React from "react"
import { useState, useEffect } from "react"
import axios from "axios"
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material"
import {
  Person as PersonIcon,
  Campaign as CampaignIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  Sync as SyncIcon,
} from "@mui/icons-material"
import { useSelector } from "react-redux"
import { API_URL } from "../../constants"

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const { userInfo } = useSelector((state) => state.auth)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        }

        const { data } = await axios.get(`${API_URL}/api/dashboard/admin`, config)
        setDashboardData(data)
        setLoading(false)
      } catch (error) {
        setError(error.response && error.response.data.message ? error.response.data.message : error.message)
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [userInfo])

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )
  }

  if (!dashboardData) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No dashboard data available.
      </Alert>
    )
  }

  const { stats, campaignPerformance, recentActivities, topCampaign } = dashboardData

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "info.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PersonIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalUsers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.totalEndUsers} end users, {stats.totalResellers} resellers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "secondary.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <WhatsAppIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Active Numbers
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.activeNumbers}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                WhatsApp numbers in use
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "success.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CampaignIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Campaigns
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalCampaigns}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  size="small"
                  icon={<CheckCircleIcon fontSize="small" />}
                  label={`${stats.completedCampaigns} completed`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  icon={<PendingIcon fontSize="small" />}
                  label={`${stats.otherCampaigns} in progress`}
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                height: 4,
                bgcolor: "warning.main",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            />
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <MessageIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h3">
                  Messages
                </Typography>
              </Box>
              <Typography variant="h3" component="div" sx={{ fontWeight: "bold", my: 1 }}>
                {stats.totalMessages.toLocaleString()}
              </Typography>
              {/* <Box sx={{ display: "flex", alignItems: "center" }}>
                <TrendingUpIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="success.main">
                  {stats.deliveryRate}% delivery rate
                </Typography>
              </Box> */}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" component="h2">
                Recent Campaign Activities
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest campaign updates
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {recentActivities && recentActivities.length > 0 ? (
                recentActivities.map((activity, index) => {
                  // Determine which icon to use based on the activity type
                  let ActivityIcon = CheckCircleIcon
                  if (activity.avatarIcon === "Pending") ActivityIcon = PendingIcon
                  else if (activity.avatarIcon === "Error") ActivityIcon = ErrorIcon
                  else if (activity.avatarIcon === "Sync") ActivityIcon = SyncIcon

                  return (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider component="li" />}
                      <ListItem sx={{ py: 2, px: 3, "&:hover": { bgcolor: "action.hover" } }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: activity.avatarColor }}>
                            <ActivityIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={activity.activityType} secondary={activity.description} />
                        <Typography variant="body2" color="text.secondary">
                          {new Date(activity.timestamp).toLocaleString()}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  )
                })
              ) : (
                <ListItem>
                  <ListItemText primary="No recent activities" />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

       
      </Grid>
    </Box>
  )
}

export default AdminDashboard
